<script setup lang="ts">
import { useProductStore } from '~/store/product/productStore';
import { money } from '~/helpers/product/productHelper';
import type { IOffer } from '~/interfaces/product/IProductPageData';
import { useVariantSelectorStore } from '~/store/variantSelectorStore';
import { MODAL_OPEN, MODAL_PRODUCT_AVAILABILITY, useModalStore } from '~/store/modalStore';
import { isMobile } from '~/helpers/utils';
import Loader from '~/components/loaders/Loader.vue';

const router = useRouter();
const productStore = useProductStore();
const variantSelectorStore = useVariantSelectorStore();
const modalStore = useModalStore();
const {
  offer,
  variantCollection,
  wasOffer,
  product,
  unavailableProductSizes
} = storeToRefs(productStore);

const {
  isOpen,
  selectedVariant,
  currentVariant
} = storeToRefs(variantSelectorStore);

const buyBox = ref(null);
const currentVariantLoading = ref(false);

const hasNewPrice = (variantCollection: IOffer[]): boolean => {
  const newPrice = variantCollection.map((variant) => variant.price.finalPrice !== offer.value.price.finalPrice)
  return newPrice.length > 0;
}

const isHiddenPrice = (newPrice: number) => {
  if (selectedVariant.value) {
    return newPrice === selectedVariant.value.price.finalPrice
  }

  if (currentVariant.value && offer.value.price.hasMinPrice) {
    return newPrice === offer.value.price.minPrice;
  }

  return newPrice === offer.value.price.finalPrice
}

const handleClick = () => {
  if (variantCollection.value.length <= 1) {
    return;
  }

  if (isOpen.value) {
    if (isMobile()) {
      modalStore.displayOverlay = false;
    }
    variantSelectorStore.close();
    return;
  }

  if (isMobile()) {
    modalStore.displayOverlay = true;
  }

  variantSelectorStore.open();
}

const displayProductAvailabilityModal = () => modalStore.displayModal(MODAL_PRODUCT_AVAILABILITY, MODAL_OPEN)

const onClickOutside = (event: Event) => {
  if (!buyBox.value ||
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      (buyBox?.value?.contains(event.target)) ||
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      document.getElementById('add-to-cart')?.contains(event.target) ||
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      document.getElementById('add-to-wishlist')?.contains(event.target)
  ) return;

  if (isMobile()) {
    modalStore.displayOverlay = false;
  }

  variantSelectorStore.close();
}

const updateSelection = (item: IOffer) => {
  currentVariantLoading.value = true;
  const id = item.canonicalMerchantProduct.id;
  const currentUrl = window.location.pathname;
  const newUrl = `${currentUrl}?o=${id}`;
  window.history.replaceState({}, '', newUrl);

  variantSelectorStore.close();

  productStore.updateMerchantProductVariant(product.value.id, item.canonicalMerchantProduct.id).then(() => {
    selectedVariant.value = item;
    if (isMobile()) {
      modalStore.displayOverlay = false;
    }
    currentVariantLoading.value = false;
  });
}

watch(variantCollection,
    (newValue) => {
      const sameSize = newValue.filter((variant) => variant.variant.value === selectedVariant.value?.variant.value);

      if (sameSize.length === 0) {
        selectedVariant.value = null;
      } else {
        selectedVariant.value = sameSize[0];
        router.push({query: {o: sameSize[0].canonicalMerchantProduct.id}})
      }
    },
    {deep: true}
)

onMounted(() => {
  document.addEventListener("click", onClickOutside, true);
})
</script>

<template>
  <form v-if="offer.variant && variantCollection.length > 0" class="buy-box__form">
    <div
         id="buybox-anchor"
         class="buy-box__anchor"/>
    <div ref="buyBox" class="buy-box__label mt-1">
      <div
           :class="`${isOpen && variantCollection.length > 1 ? 'open' : ''} buy-box__select select`"
           data-tclz-selector="variant-select"
           :data-tclz-label="offer.variant.displayName"
           data-tclz-category="product"
           @click="handleClick" >
        <span class="select-name pe-n">{{ offer.variant.displayName }} : </span>
        <Loader
            v-if="currentVariantLoading"
            color="primary"
            size="tiny" />
        <span v-else-if="currentVariant" class="select-value pe-n">
          {{ currentVariant.variant.value }}
        </span>
        <div v-if="variantCollection.length > 1" class="select-arrow pe-n">
          <svg class="cicon cicon--bold">
            <use xlink:href="#bottom-v2"/>
          </svg>
        </div>
      </div>

      <div v-if="variantCollection.length > 1" :class="`${isOpen ? 'open' : ''} buy-box__variant-list`">
        <div class="buy-box__variant-list-title">
          <span class="visible-mobile">{{ offer.variant.displayName }}</span>
          <svg class="cicon--24 cicon--s-dark cicon visible-mobile" @click="handleClick">
            <use xlink:href="#close-v2"/>
          </svg>
        </div>
        <div class="buy-box__variant-list-content">
          <div
               v-if="hasNewPrice(variantCollection)"
               class="buy-box__variant-price size--caption weight--interregular c-text-primary">
            <span>{{ $t('www.product.size_available_other_merchant') }}</span>
          </div>
          <div
               v-for="(item, index) in variantCollection"
               :key="index"
               class="buy-box__variant variant-option-js df aic jcsb cbtn"
               data-tclz-selector="variant-item"
               :data-tclz-label="offer.variant.displayName"
               data-tclz-category="product"
               @click="updateSelection(item)">
            <div class="price">
                <span :style="`${isHiddenPrice(item.price.finalPrice) ? 'display:none' : ''}`">
                    {{ money(item.price.finalPrice) }}
                </span>
            </div>
            <div class="value">
              <span class="weight--intermedium">{{ item.variant.value }}</span>
            </div>
            <div class="buy-box__stock">
                <span v-if="item.availableStock < 3">
                  {{ $t('www.product.stock_soon_sold_out', {
                    'count': item.availableStock > 1 ? 2 : 1,
                    'stock': item.availableStock
                  }) }}
                </span>
            </div>
          </div>
          <div
              v-if="unavailableProductSizes.length > 0"
              class="pointer buy-box__variant"
              data-tclz-selector="availability-modal"
              :data-tclz-label="product.categoryPath"
              data-tclz-category="product"
              data-tclz-action="email alert click"
              @click="displayProductAvailabilityModal">
              <span class="clink clink--primary ">{{ $t('www.product_availability.label') }}</span>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>
